/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/_shadcn-ui/popover';
import AutocompleteTrigger from './autocomplete-trigger';
import AutocompleteList from './autocomplete-list';
import AutocompleteSearch from './autocomplete-search';
import { cn } from '@/utils/cn';

export type GenericOption<T> = T;

interface AutocompleteProps<T> {
  id?: string;
  options: GenericOption<T>[];
  value?: GenericOption<T> | null;
  defaultValue?: GenericOption<T> | null;
  onChange: (value: GenericOption<T> | null) => void;
  disabled?: boolean;

  // Optional UI configs
  placeholder?: string;
  searchPlaceholder?: string;
  isLoading?: boolean;
  isWithDeleteButton?: boolean;
  triggerClassName?: string;

  // Custom keys
  labelKey: keyof T;
  valueKey: keyof T;

  // Renderers
  onSearch?: (value: string) => void;
  onClear?: () => void;
  renderOption?: (option: GenericOption<T>, isSelected: boolean) => React.ReactNode;
  renderSelectedValue?: (option: GenericOption<T>) => React.ReactNode;
}

export default function Autocomplete<T extends Record<string, any>>({
  id,
  options,
  value,
  defaultValue,
  disabled = false,

  // Optional UI configs
  isLoading = false,
  placeholder = 'Select an option',
  searchPlaceholder = 'Search...',

  // Custom Keys
  labelKey,
  valueKey,
  isWithDeleteButton = true,
  triggerClassName,

  // Renderers
  onChange,
  onSearch,
  onClear,
  renderOption,
  renderSelectedValue,
}: AutocompleteProps<T>) {
  // HOOKS
  const [isOpen, setIsOpen] = useState(false);
  const [internalSearch, setInternalSearch] = useState('');
  const [internalValue, setInternalValue] = useState<GenericOption<T> | null>(defaultValue ?? null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // HELPERS
  // get label logic, get the label of the option by the label key
  const getLabel = useCallback((option: T) => String(option[labelKey] ?? ''), [labelKey]);

  // get value logic, get the value of the option by the value key
  const getValue = useCallback(
    (option: T): string | number => {
      const value = option[valueKey];
      return typeof value === 'string' || typeof value === 'number' ? value : String(value);
    },
    [valueKey]
  );

  // is selected logic, check if the option is selected by comparing the value of the option with the value of the internal value
  const isSelected = useCallback(
    (option: T) => Boolean(internalValue && getValue(option) === getValue(internalValue)),
    [internalValue, getValue]
  );

  // filtered options logic, filter the options based on the search query
  const filteredOptions = useMemo(() => {
    if (!internalSearch.trim()) return options;
    return options.filter((opt) => getLabel(opt).toLowerCase().includes(internalSearch.toLowerCase()));
  }, [options, internalSearch, getLabel]);

  // EFFECTS
  useEffect(() => setInternalValue(value ?? null), [value]);

  // Cleanup timeout on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // HANDLERS
  // select logic, select the option and close the dropdown
  const handleSelect = useCallback(
    (option: T) => {
      setInternalValue(option);
      onChange(option);
      setIsOpen(false);
    },
    [onChange]
  );

  // clear logic, clear the selected option and close the dropdown
  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setInternalValue(null);
      onChange(null);
      onClear?.();
    },
    [onChange, onClear]
  );

  // open/close dropdown logic, pass the open state to the handler
  const handleOpenChange = useCallback((open: boolean) => {
    setIsOpen(open);
    // Clear any existing timeout to prevent memory leaks
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    // Set a new timeout to clear search when dropdown closes
    timeoutRef.current = setTimeout(() => {
      setInternalSearch('');
      timeoutRef.current = null;
    }, 100);
  }, []);

  // search logic, pass the value to the handler
  const handleInternalSearch = useCallback(
    (value: string) => {
      setInternalSearch(value);
      onSearch?.(value);
    },
    [onSearch]
  );

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild className="w-full" disabled={disabled}>
        <AutocompleteTrigger
          isOpenList={isOpen}
          selected={internalValue}
          placeholder={placeholder}
          disabled={disabled}
          renderSelectedItem={renderSelectedValue}
          getLabel={getLabel}
          onClear={handleClear}
          isWithDeleteButton={isWithDeleteButton}
          triggerClassName={triggerClassName}
        />
      </PopoverTrigger>

      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0 overflow-hidden rounded-[8px] border border-[#D9D9D9]">
        <div className="flex flex-col">
          {/* Search input */}
          <div className="p-[8px]">
            <AutocompleteSearch
              id={id ?? 'autocomplete-search'}
              searchPlaceholder={searchPlaceholder}
              search={internalSearch}
              onSearch={handleInternalSearch}
            />
          </div>

          {/* Virtualized list */}
          <AutocompleteList<T>
            isLoading={isLoading}
            options={filteredOptions}
            isSelected={isSelected}
            getLabel={getLabel}
            onSelect={handleSelect}
            renderOption={renderOption}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}
